import { ApolloProvider } from '@apollo/client';
import {
  assertUnreachable,
  LabelAssetType,
  SiteUrlsContext,
  useRenderLabels,
  UseRenderLabelsParams,
  UseRenderLabelsResult } from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { QueryClient} from '@tanstack/react-query';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { useSelector } from '~src/settings/general-settings/utils';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { catalogApolloClient } from './CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from './CatalogFeatureFlagsAdapter';
import { CERTIFIED_INDICATOR_TYPE, WARNING_INDICATOR_TYPE } from './components/labels/DataQualityType';
import { urlForContentType } from './components/listView/DataQualityIndicatorCell';
import { getTelemetryCommonProperties } from './DataCatalogTelemetry';


export type LabelWrapperProps = {
  readonly category: 'warning' | 'certification' | 'all';
  readonly itemInfo: IContentInfo<Extract<ExplorableItemType, LabelAssetType>>;
  readonly masthead: boolean;
  readonly kind: 'icon' | 'chip';
}

export function LabelWrapper(props: LabelWrapperProps): JSX.Element | null {
  const urls = useSelector(siteUrlsSelector);

  return (
  <SiteUrlsContext.Provider value={urls}>
    <ApolloProvider client={catalogApolloClient}>
      <LabelWrapperInner {...props} />
    </ApolloProvider>
  </SiteUrlsContext.Provider>);
}

function LabelWrapperInner(props: LabelWrapperProps): JSX.Element | null {

  const catalogingOnSiteEnabled = useSelector(SessionSiteSelectors.catalogingOnSiteEnabled);

  const { certifications, warnings } = useRenderLabelsVpc(props);

  if (!catalogingOnSiteEnabled) {
    return null;
  }

  switch (props.category) {
    case 'certification': return (certifications ? certifications : null);
    case 'warning': return (warnings ? warnings : null);
    default:
      assertUnreachable(props.category);
      return null;
  }
}

type UseRenderLabelsVpcParams = UseRenderLabelsParams & { readonly urls: SiteUrls }


function buildProbablyOrEager({ masthead, category, itemInfo }: Pick<LabelWrapperProps, 'category' | 'itemInfo' | 'masthead'>): UseRenderLabelsParams['probablyOrEager'] {
  if (!FeatureFlags.labelsInMorePlacesEnabled) {
    return {};
  }

  if (masthead) {
    return 'eager';
  }

  const activeLabels = itemInfo.dataQualityIndicators?.filter(t => t.isActive) ?? [];

  if (activeLabels.length === 0) {
    return {};
  }

  const hasActiveCertification = activeLabels.some(t => t.type === CERTIFIED_INDICATOR_TYPE);
  const hasActiveWarning = activeLabels.some(t => t.type === WARNING_INDICATOR_TYPE);
  const hasSevereWarning = activeLabels.some(t => t.isSevere && t.type === WARNING_INDICATOR_TYPE);

  switch (category) {
    case 'certification': return {
      certified: hasActiveCertification
    };
    case 'warning':
      return {
        warning: hasActiveWarning ? {
          kind: 'warning',
          severe: hasSevereWarning,
        } : undefined,
      };
    case 'all': {
      return {
        certified: hasActiveCertification,
        warning: hasActiveWarning ? {
          kind: 'warning',
          severe: hasSevereWarning,
        } : undefined,
      };
    }
    default:
      assertUnreachable(category);
  }
}

export function useRenderLabelsVpc({ category, itemInfo, masthead, kind }: LabelWrapperProps): UseRenderLabelsResult {

  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);
  const catalogEnabled = useSelector(SessionSiteSelectors.catalogingOnSiteEnabled);
  const urls = useSelector(siteUrlsSelector);
  const [telemetryContext] = React.useState(() => getTelemetryCommonProperties());
  const probablyOrEager = buildProbablyOrEager({ masthead, category, itemInfo });
  const catalogQueryClient = new QueryClient();

  const params: UseRenderLabelsVpcParams = {
    includeUpstream: !!masthead,
    includeParented: false,
    probablyOrEager: probablyOrEager,
    catalogEnabled,
    content: {
      contentId: itemInfo.id,
      contentLuid: itemInfo.luid,
      contentType: itemInfo.contentType,
      contentName: itemInfo.name,
      contentUrl: urlForContentType(urls, itemInfo),
      subjectArea: itemInfo.contentType,
    },
    testId: itemInfo.contentType + (masthead ? 'masthead' : 'card'),
    contextOverrides: {
      queryClient: catalogQueryClient,
      restApiServiceParams: { siteLuid },
      siteUrls: urls,
      telemetryContext,
      featureFlags: catalogFeatureFlagsAdapter(),
    },
    controls: {
      kind: kind,
      siteUrls: urls,
    },
    urls,
  };

  return useRenderLabels(params);
}
